<script setup async>
import {onMounted, onBeforeUnmount, ref} from "vue";
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import AccessApplicationCell from "~/components/users/AccessApplicationCell.vue";
import NoResults from "~/components/loaders/NoResults.vue";
import FixtureResultRow from "~/components/fixtures/FixtureResultRow.vue";
import LeagueClickableCell from "~/components/lists/LeagueClickableCell.vue";
import AmericanoClickableCell from "~/components/lists/AmericanoClickableCell.vue";
import LadderClickableCell from "~/components/lists/LadderClickableCell.vue";
import MeetClickableCell from "~/components/lists/MeetClickableCell.vue";
import PlayerList from "~/components/players/PlayerList.vue";
import PlayerCard from "~/components/players/PlayerCard.vue";
import ClickableCell from "~/components/lists/ClickableCell.vue";
import {useValidation} from "~/composables/useValidation";
import {useMessaging} from "~/composables/useMessaging";
import {UNBLOCK_TO_MESSAGE_LABEL, messageActionFor} from "~/composables/useMessagePermission.js";
import {useSafety} from "~/composables/useSafety";
import {createError} from "nuxt/app";
import TournamentClickableCell from "~/components/lists/TournamentClickableCell.vue";
import AvatarImage from "~/components/images/AvatarImage.vue";

definePageMeta({
  name: 'PlayerDetail',
  path: '/players/:playerId',
});

const route = useRoute();
const playerId = route.params.playerId;
const {item: page, getItem: getPage, hasItemErrors: hasPageErrors} = useCrudApi("api/v2/page/player");
await getPage(route.params.playerId);
if (hasPageErrors.value) {
  throw createError({
    statusCode: 404, // or another relevant status code
    message: 'Player not found'
  });
}

const {t} = useI18n()
const {setMeta} = useHeadService();
// A private profile names nobody in its meta (PRIVACY_PLAN.md §4.1); a Hidden
// person's stub already carries "Private player", but the title stays generic.
const pagePrivate = computed(() => !!page.value?.privateToViewer);
usePrivatePage(pagePrivate);
if (pagePrivate.value) {
  setMeta('Player profile on Tap Leagues', 'A player profile on Tap Leagues.');
} else {
  setMeta(
      page.value?.player?.fullName + ' · Player Profile on Tap Leagues',
      page?.value?.player?.fullName + ' is a player on Tap Leagues. '
  );
}

// Follow from the private notice (PRIVACY_PLAN.md §3.6): one request, then the page re-reads its status.
const {request: requestFollow, accept: acceptFollow, list: listFollows, reasonOf: followReason} = useFollows();
const {showError: showFollowError} = useAlertService();
const followBusy = ref(false);
const followStatus = computed(() => page.value?.player?.followStatus ?? null);
// On an OPEN profile (public, or a club mate's) the same button sits beside Message:
// a public profile is still somebody outside your clubs until you follow each other.
const followOffered = computed(() => ['none', 'requested', 'requestReceived'].includes(followStatus.value));
const followLabel = computed(() => followStatus.value === 'requested' ? 'Requested'
    : followStatus.value === 'requestReceived' ? 'Accept their request' : 'Follow'); // one click here: handleFollow accepts it
// A managed player (no account): a club admin answers follow requests for them.
// Only when we can actually see the player: a private profile's stub hides
// the account, and reading "no account" off it called a Private player
// "admin managed" to people outside their clubs (13 Oct 2026).
const managedPlayer = computed(() => !!page.value?.player && !page.value.player.obfuscated
    && page.value.player.userId == null);
// On a locked profile the stub can't say, so once the viewer has asked, their own
// sent request does: it says whether a club admin answers it (needsAdminApproval).
// The API already told them so when they asked, so this reveals nothing new.
const requestNeedsAdmin = ref(false);
const loadRequestNeedsAdmin = async () => {
  requestNeedsAdmin.value = false;
  if (followStatus.value !== 'requested' || !isAuthenticated.value) return;
  try {
    const mine = await listFollows();
    requestNeedsAdmin.value = !!(mine || []).find(f => f.direction === 'sent' && f.status === 0
        && f.person?.playerId === playerId)?.needsAdminApproval;
  } catch {
    requestNeedsAdmin.value = false;
  }
};
onMounted(() => watch(followStatus, loadRequestNeedsAdmin, {immediate: true}));
const privateName = computed(() => {
  const p = page.value?.player;
  if (!p || p.fullName === 'Private player') return '';
  return p.fullName;
});
const handleFollow = async () => {
  if (followBusy.value) return;
  followBusy.value = true;
  try {
    if (followStatus.value === 'requestReceived') {
      const mine = await listFollows();
      const received = (mine || []).find(f => f.direction === 'received' && f.status === 0 && f.person?.playerId === playerId);
      if (received) await acceptFollow(received.id);
    } else {
      await requestFollow(playerId);
    }
    await getPage(playerId);
  } catch (e) {
    showFollowError(followReason(e));
  } finally {
    followBusy.value = false;
  }
};
const {isAuthenticated, user, logout, isAdminOfClub} = useAuthService();
const router = useRouter();

const {createConversation} = useMessaging();
const messageLoading = ref(false);

const handleMessage = async () => {
  if (!page.value?.player?.userId) return;

  try {
    messageLoading.value = true;
    const conversation = await createConversation([page.value.player.userId], 1, null);
    await router.push({
      query: {...route.query, conversationId: conversation.id, messaging: 'open'}
    });
  } catch (error) {
    console.error('Failed to create conversation:', error);
  } finally {
    messageLoading.value = false;
  }
};

// Can the viewer send a message to this player? The API's canMessage, NOT
// "not obfuscated": hidden contact details only hide email and phone
// (release fixes, 28 Sep 2026).
// Someone the viewer blocked gets "Unblock to message" instead (29 Sep 2026).
const {blockedUserIds, loadBlocked, unblock} = useSafety();
onMounted(() => {
  if (isAuthenticated.value) loadBlocked();
});
const messageAction = computed(() =>
  messageActionFor(page.value?.player, {
    signedIn: isAuthenticated.value, myUserId: user.value?.id, blockedUserIds: blockedUserIds.value,
  })
);
const canMessage = computed(() => messageAction.value === 'message');

// Unblock, then start the chat as Message would. A refusal is shown by
// createConversation and nothing opens.
const handleUnblockToMessage = async () => {
  if (!page.value?.player?.userId) return;
  try {
    messageLoading.value = true;
    await unblock(page.value.player.userId);
  } catch (error) {
    messageLoading.value = false;
    return;
  }
  await handleMessage();
};

const titleActions = computed(() => {
  const actions = [];

  if (user.value && user.value.playerId === playerId) {
    actions.push({
      title: 'Edit my profile',
      icon: 'mdi-pencil',
      route: {name: 'YourProfile'},
      color: 'info',
    });
  }

  if (canMessage.value) {
    actions.push({
      title: 'Message',
      icon: 'mdi-chat',
      color: 'success',
      click: handleMessage,
      loading: messageLoading.value
    });
  } else if (messageAction.value === 'unblock') {
    actions.push({
      title: UNBLOCK_TO_MESSAGE_LABEL,
      icon: 'mdi-account-lock-open-outline',
      color: 'success',
      click: handleUnblockToMessage,
      loading: messageLoading.value
    });
  }

  actions.push({
    title: t("view_fixtures", 0),
    icon: 'mdi-scoreboard',
    route: {
      name: 'FixtureSearch',
      query: {playerIds: [playerId]}
    },
    color: 'black',
  });

  return actions;
});
const {isAdminControlledPlayer} = useValidation();
const {addListener, removeLocalListenersWithEvent} = useListenerService();
onMounted(() => {
  addListener("requestsUpdated", () => getPage(route.params.playerId));
});
onBeforeUnmount(() => {
  removeLocalListenersWithEvent("requestsUpdated");
});

// Derived presentation helpers (presentation only — no data-fetching changes)
const playerInitials = computed(() =>
  (page.value?.player?.initials || page.value?.player?.fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?'
);

// Primary club name for profile head subtitle (first club in the list, if any)
const primaryClubName = computed(() => page.value?.clubs?.[0]?.name || null);

// Show squashLevel chip only when there's a real positive rating
const hasSquashLevel = computed(() =>
  page.value?.player?.squashLevel != null && page.value.player.squashLevel > 0
);

// Whether the competitions section has any entries (meets/americanos are
// version-gated on the API and may be missing from the response)
const hasCompetitions = computed(() =>
  (page.value?.leagues?.length || 0) +
  (page.value?.americanos?.length || 0) +
  (page.value?.ladders?.length || 0) +
  (page.value?.tournaments?.length || 0) +
  (page.value?.meets?.length || 0) > 0
);

</script>

<template>
  <base-page>
    <!-- Private for this viewer (PRIVACY_PLAN.md §3.3): a short name at most, and a way to ask -->
    <v-container v-if="pagePrivate" class="large">
      <!-- The same shape as every other locked page (PRIVACY_PLAN.md §14): an
           identity header, then the notice where the profile would be. A hidden
           person has no name to show, so the header says Player profile. -->
      <frontend-header class="mb-8" :title="privateName || 'Player profile'" title-icon="mdi-account-outline" hide-breadcrumbs>
        <template #subTitle>
          <span class="tl-chip">{{ privateName ? 'Player profile' : 'Private player' }}</span>
        </template>
      </frontend-header>
      <private-notice reason="player" :name="privateName" :follow-status="followStatus" :follow-busy="followBusy" :managed="managedPlayer || requestNeedsAdmin" @follow="handleFollow"/>
    </v-container>
    <v-container v-else>
      <div class="player-page">

        <!-- ======== BREADCRUMBS ======== -->
        <div class="tl-breadcrumbs" v-if="page.clubs?.length">
          <nuxt-link :to="{ name: 'ClubSearch' }">Clubs</nuxt-link>
          <v-icon size="14" class="tl-breadcrumbs__sep">mdi-chevron-right</v-icon>
          <nuxt-link :to="{ name: 'ClubDetail', params: { clubId: page.clubs[0].slug } }">
            {{ page.clubs[0].name }}
          </nuxt-link>
          <v-icon size="14" class="tl-breadcrumbs__sep">mdi-chevron-right</v-icon>
          <span class="tl-breadcrumbs__here">{{ page.player.fullName }}</span>
        </div>

        <v-row align="start">

          <!-- ======== MAIN COLUMN ======== -->
          <v-col cols="12" md="8" order="last" order-md="first">

            <!-- PROFILE HEAD CARD -->
            <div class="tl-card profile-head">
              <avatar-image
                class="profile-head__avatar"
                :size="72"
                :filename="page.player.imageFileName"
                :fallback="playerInitials"
              />
              <div class="profile-head__who">
                <div class="profile-head__name">{{ page.player.fullName }}</div>
                <!-- Their user tag (PRIVACY_PLAN.md §15): what a friend types to find them. Only served to viewers who see them in full. -->
                <div v-if="page.player.tag" class="profile-head__tag">@{{ page.player.tag }}</div>
                <div class="profile-head__meta" v-if="primaryClubName">
                  {{ primaryClubName }}
                </div>
                <div v-if="isAdminControlledPlayer(page.player)" class="profile-head__chips">
                  <v-chip size="x-small" color="warning" class="tl-chip tl-chip--warn">
                    Admin managed
                  </v-chip>
                </div>
                <div v-else-if="hasSquashLevel" class="profile-head__chips">
                  <span class="tl-chip">
                    <v-icon size="13">mdi-gauge</v-icon>
                    {{ page.player.squashLevel }}
                  </span>
                </div>
              </div>
            </div>

            <!-- PENDING APPLICATIONS -->
            <template v-if="page.applications.length > 0">
              <div class="tl-section-head mt-0"><h2>Requiring your attention</h2></div>
              <div class="tl-card mb-4">
                <access-application-cell
                    v-for="application in page.applications"
                    :key="application.id"
                    :request="application"
                ></access-application-cell>
              </div>
            </template>

            <!-- RECENT RESULTS: played matches only, never ones still to come (14 Oct 2026) -->
            <crud-list-loader path="api/v2/fixtures"
                              :listeners="['fixtureUpdated']"
                              :per-page="5"
                              :custom-filters="{ playerIds: [playerId], isPlayed: true }">
              <template #loader="{empty}">
                <div v-if="empty" class="tl-section-head mt-8"><h2>Recent results</h2></div>
                <v-skeleton-loader v-if="empty" class="mb-4" height="187"></v-skeleton-loader>
                <v-skeleton-loader v-if="empty" class="mb-4" height="187"></v-skeleton-loader>
                <v-skeleton-loader v-if="empty" class="mb-4" height="187"></v-skeleton-loader>
              </template>
              <template #empty>
                <div class="tl-section-head mt-8"><h2>Recent results</h2></div>
                <no-results>
                  {{ t('player_no_fixtures') }}
                </no-results>
              </template>
              <template #default="{ items : latestFixtures }">
                <div class="tl-section-head mt-8">
                  <h2>Recent results</h2>
                  <nuxt-link :to="{ name: 'PlayerStats', params: { playerId: playerId } }">View all</nuxt-link>
                </div>
                <no-results v-if="latestFixtures.length === 0">
                  {{ t('club_no_fixtures') }}
                </no-results>
                <template v-if="latestFixtures.length > 0">
                  <fixture-result-row
                      v-for="fixture in latestFixtures"
                      :key="fixture.id"
                      :fixture="fixture"
                  />
                </template>
              </template>
            </crud-list-loader>

          </v-col>

          <!-- ======== SIDE COLUMN ======== -->
          <v-col cols="12" md="4" order="first" order-md="last">
            <div class="player-side-sticky">

              <!-- FOLLOW (PRIVACY_PLAN.md §3.6): someone outside your clubs, whatever their profile setting -->
              <div class="player-side-actions" v-if="followOffered">
                <v-btn
                    block
                    variant="outlined"
                    class="player-side-btn"
                    :disabled="followStatus === 'requested'"
                    :loading="followBusy"
                    prepend-icon="mdi-account-heart-outline"
                    @click="handleFollow"
                >
                  {{ followLabel }}
                </v-btn>
              </div>

              <!-- MESSAGE BUTTON (challenges only happen inside a ladder, so no direct challenge here) -->
              <div class="player-side-actions" v-if="canMessage">
                <v-btn
                    block
                    variant="outlined"
                    class="player-side-btn"
                    :loading="messageLoading"
                    @click="handleMessage"
                    prepend-icon="mdi-chat"
                >
                  Message {{ page.player.firstName?.trim() || page.player.fullName }}
                </v-btn>
              </div>
              <div class="player-side-actions" v-else-if="messageAction === 'unblock'">
                <v-btn
                    block
                    variant="outlined"
                    class="player-side-btn"
                    :loading="messageLoading"
                    @click="handleUnblockToMessage"
                    prepend-icon="mdi-account-lock-open-outline"
                >
                  {{ UNBLOCK_TO_MESSAGE_LABEL }}
                </v-btn>
              </div>

              <!-- STATS -->
              <div class="tl-section-head mt-0 mt-sm-4"><h2>Stats</h2></div>
              <div class="tl-card player-statgrid">
                <div class="player-statcard">
                  <div class="player-statcard__v">{{ page.fixtureCount }}</div>
                  <div class="player-statcard__k">{{ t('fixtures_played') }}</div>
                </div>
                <div class="player-statcard">
                  <div class="player-statcard__v text-success">{{ page.fixtureWonCount }}</div>
                  <div class="player-statcard__k">{{ t('fixtures_won') }}</div>
                </div>
                <div class="player-statcard">
                  <div class="player-statcard__v">{{ page.fixtureLostCount }}</div>
                  <div class="player-statcard__k">{{ t('fixtures_lost') }}</div>
                </div>
                <nuxt-link
                    class="player-statcard player-statcard--link"
                    :to="{ name: 'PlayerStats', params: { playerId: playerId } }"
                >
                  <div class="player-statcard__v">
                    <v-icon size="20" icon="mdi-chart-timeline-variant" />
                  </div>
                  <div class="player-statcard__k">
                    Full stats
                    <v-icon size="11" icon="mdi-chevron-right" />
                  </div>
                </nuxt-link>
              </div>

              <!-- CLUBS (Home side-rail style — compact, no Manage button) -->
              <template v-if="page.clubs.length > 0">
                <div class="tl-section-head"><h2>Clubs</h2></div>
                <clickable-cell
                    v-for="club in page.clubs"
                    :key="club.id"
                    tone="neutral"
                    :image-file-name="club.imageFileName"
                    icon="mdi-account-group-outline"
                    class="mb-3"
                    :title="club.name"
                    :subtitle="(club.playerCount ?? 0) === 1 ? '1 member' : `${club.playerCount ?? 0} members`"
                    :to="{ name: 'ClubDetail', params: { clubId: club.slug } }"
                />
              </template>

              <!-- COMPETITIONS (leagues + americanos + ladders + tournaments + meets) -->
              <template v-if="hasCompetitions">
                <div class="tl-section-head"><h2>Competitions</h2></div>
                <league-clickable-cell
                    v-for="league in page.leagues"
                    :key="league.id"
                    :league="league"
                ></league-clickable-cell>
                <americano-clickable-cell
                    v-for="americano in page.americanos ?? []"
                    :key="americano.id"
                    :americano="americano"
                ></americano-clickable-cell>
                <ladder-clickable-cell
                    v-for="ladder in page.ladders"
                    :key="ladder.id"
                    :ladder="ladder"
                ></ladder-clickable-cell>
                <tournament-clickable-cell
                    v-for="tournament in page.tournaments"
                    :key="tournament.id"
                    :tournament="tournament"
                ></tournament-clickable-cell>
                <meet-clickable-cell
                    v-for="meet in page.meets ?? []"
                    :key="meet.id"
                    :meet="meet"
                ></meet-clickable-cell>
              </template>

            </div>
          </v-col>

        </v-row>
      </div>
    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
.player-page {
  padding-bottom: 48px;
}

/* ---- Breadcrumbs ---- */
.tl-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-textSubtle));
  margin-bottom: 14px;
  flex-wrap: wrap;

  a {
    color: rgb(var(--v-theme-textSubtle));
    text-decoration: none;

    &:hover {
      color: rgb(var(--v-theme-text));
    }
  }
}

.tl-breadcrumbs__sep {
  color: #D5D5D5;
}

.tl-breadcrumbs__here {
  color: rgb(var(--v-theme-charcoal));
}

/* ---- Profile head card (mockup .card.profile-head) ---- */
.profile-head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 16px;
  margin-bottom: 0;
}

.profile-head__avatar {
  flex-shrink: 0;
  border-radius: 50%;
}

.profile-head__who {
  min-width: 0;
  flex: 1;
}

.profile-head__tag { font-size: 14px; font-weight: 500; color: rgba(0, 0, 0, 0.55); margin-top: 2px; }
.profile-head__name {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.4px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-head__meta {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 3px;
}

.profile-head__chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
}

/* ---- Side action buttons (mockup .btn-block) ---- */
.player-side-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.player-side-btn {
  border-radius: 999px !important;
  font-size: 13.5px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  height: 48px !important;
}

/* ---- Stat summary grid (side column) ---- */
.player-statgrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  padding: 0;
  overflow: hidden;
}

.player-statcard {
  padding: 14px 16px;
  border-right: 1px solid rgb(var(--v-theme-borderLight));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));

  &:nth-child(2n) {
    border-right: none;
  }

  &:nth-last-child(-n+2) {
    border-bottom: none;
  }
}

.player-statcard__v {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
  line-height: 1.3;
}

.player-statcard__k {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-top: 2px;
}

.player-statcard--link {
  display: block;
  text-decoration: none;
  transition: background 0.15s ease;

  .player-statcard__v,
  .player-statcard__k {
    color: rgb(var(--v-theme-primary));
  }

  &:hover {
    background: rgb(var(--v-theme-brandTint));
  }
}
</style>
